// The tones of the mono look, as SVG patterns, the glint of a royal piece, and the covers of a stopped piece and an uncapturable piece. A pattern is a two by two
// tile of cells, and a tone is how many of the four are on. Any SVG shape on
// the page can fill with one by id. `mono.css` decides which shapes do.

import {
  DITHER_CASTER_MARK_ID,
  DITHER_CELL_PX,
  DITHER_GLINT_IDS,
  DITHER_STOPPED_IDS,
  DITHER_TONES,
  DITHER_UNCAPTURABLE_IDS,
  MARK_TONES,
  markDitherId,
  puffDitherId,
} from '../constants';
import type { Owner, Side } from '../engine/types';
import { svg } from '../lib/svg';

const TILE_CELLS = 2;
const TILE_PX = DITHER_CELL_PX * TILE_CELLS;

/** The order the cells of a tile turn on: opposite corners first, so half is a checker. */
const CELL_ORDER: readonly (readonly [number, number])[] = [
  [0, 0],
  [1, 1],
  [1, 0],
  [0, 1],
];

const cell = ([column, row]: readonly [number, number], className = 'dither__on'): SVGRectElement =>
  svg('rect', {
    class: className,
    x: column * DITHER_CELL_PX,
    y: row * DITHER_CELL_PX,
    width: DITHER_CELL_PX,
    height: DITHER_CELL_PX,
  });

const tonePattern = (id: string, cellsOn: number): SVGPatternElement =>
  svg('pattern', { id, width: TILE_PX, height: TILE_PX, patternUnits: 'userSpaceOnUse' }, [
    svg('rect', { class: 'dither__off', width: TILE_PX, height: TILE_PX }),
    ...CELL_ORDER.slice(0, cellsOn).map((place) => cell(place)),
  ]);

/** Half of the cells and no ground, so the piece shows between them. */
const glintPattern = (side: Side): SVGPatternElement =>
  svg(
    'pattern',
    { id: DITHER_GLINT_IDS[side], width: TILE_PX, height: TILE_PX, patternUnits: 'userSpaceOnUse' },
    CELL_ORDER.slice(0, 2).map((place) => cell(place, `dither__glint dither__glint--${side}`)),
  );

/**
 * Half of the cells and no ground, so a stopped piece keeps the color of its
 * side. The mono look hides the second cell on Sun, so a stopped Sun piece is
 * more white than a stopped Moon piece.
 */
const stoppedPattern = (side: Side): SVGPatternElement =>
  svg(
    'pattern',
    {
      id: DITHER_STOPPED_IDS[side],
      width: TILE_PX,
      height: TILE_PX,
      patternUnits: 'userSpaceOnUse',
    },
    [
      cell(CELL_ORDER[0], `dither__stopped dither__stopped--${side}`),
      cell(CELL_ORDER[1], `dither__stopped dither__stopped--${side} dither__stopped--second`),
    ],
  );

const LINE_TILE_ROWS = 4;
const SECOND_LINE_ROW = 2;

const line = (row: number, className: string): SVGRectElement =>
  svg('rect', {
    class: className,
    x: 0,
    y: row * DITHER_CELL_PX,
    width: TILE_PX,
    height: DITHER_CELL_PX,
  });

/**
 * Level lines, one cell tall, on every second row and no ground. A stopped
 * piece wears a checker, so the two covers differ in shape. The mono look
 * hides the second line on Sun, as it hides the second cell of the checker.
 */
const uncapturablePattern = (side: Side): SVGPatternElement =>
  svg(
    'pattern',
    {
      id: DITHER_UNCAPTURABLE_IDS[side],
      width: TILE_PX,
      height: LINE_TILE_ROWS * DITHER_CELL_PX,
      patternUnits: 'userSpaceOnUse',
    },
    [
      line(0, `dither__uncapturable dither__uncapturable--${side}`),
      line(
        SECOND_LINE_ROW,
        `dither__uncapturable dither__uncapturable--${side} dither__uncapturable--second`,
      ),
    ],
  );

/** The cells of a band of a mark on a square, with no ground, so the square shows between them. */
const markPattern = (tone: string, paint: 'half' | 'quarter'): SVGPatternElement =>
  svg(
    'pattern',
    {
      id: markDitherId(tone, paint),
      width: TILE_PX,
      height: TILE_PX,
      patternUnits: 'userSpaceOnUse',
    },
    CELL_ORDER.slice(0, paint === 'half' ? 2 : 1).map((place) =>
      cell(place, `dither__mark dither__mark--${tone}`),
    ),
  );

/** The cells of a puff of a cloud, in the color of the owner, with no ground. */
const puffPattern = (owner: Owner, paint: 'half' | 'quarter'): SVGPatternElement =>
  svg(
    'pattern',
    {
      id: puffDitherId(owner, paint),
      width: TILE_PX,
      height: TILE_PX,
      patternUnits: 'userSpaceOnUse',
    },
    CELL_ORDER.slice(0, paint === 'half' ? 2 : 1).map((place) =>
      cell(place, `dither__puff dither__puff--${owner}`),
    ),
  );

/** A cell of the dither of a caster symbol, in the units of the box of the symbol. */
const CASTER_MARK_CELL = 1;

/**
 * Half of the cells on a black ground, for the second part of a caster symbol
 * in the mono look. The second part lies over the white tile and over its
 * black cuts, so it needs the ground to show on both. The tile is in the
 * units of the symbol, so the cells grow with the symbol.
 */
const casterMarkPattern = (): SVGPatternElement =>
  svg(
    'pattern',
    {
      id: DITHER_CASTER_MARK_ID,
      width: CASTER_MARK_CELL * TILE_CELLS,
      height: CASTER_MARK_CELL * TILE_CELLS,
      patternUnits: 'userSpaceOnUse',
    },
    [
      svg('rect', {
        class: 'dither__off',
        width: CASTER_MARK_CELL * TILE_CELLS,
        height: CASTER_MARK_CELL * TILE_CELLS,
      }),
      ...CELL_ORDER.slice(0, 2).map(([column, row]) =>
        svg('rect', {
          class: 'dither__on',
          x: column * CASTER_MARK_CELL,
          y: row * CASTER_MARK_CELL,
          width: CASTER_MARK_CELL,
          height: CASTER_MARK_CELL,
        }),
      ),
    ],
  );

const OWNERS: readonly Owner[] = ['gold', 'violet', 'neutral'];
const SIDES: readonly Side[] = ['gold', 'violet'];

/** One hidden SVG that holds the patterns. Mount it once, anywhere on the page. */
export const createDitherDefs = (): SVGSVGElement =>
  svg('svg', { class: 'dither-defs', width: 0, height: 0, 'aria-hidden': 'true' }, [
    svg('defs', {}, [
      ...DITHER_TONES.map((tone) => tonePattern(tone.id, tone.cellsOn)),
      casterMarkPattern(),
      ...SIDES.map(glintPattern),
      ...SIDES.map(stoppedPattern),
      ...SIDES.map(uncapturablePattern),
      ...OWNERS.flatMap((owner) => [puffPattern(owner, 'half'), puffPattern(owner, 'quarter')]),
      ...MARK_TONES.flatMap((tone) => [markPattern(tone, 'half'), markPattern(tone, 'quarter')]),
    ]),
  ]);
